import React, { useState } from "react";
import {
  useCreateCompanyPositionMutation,
  useUpdateCompanyPositionMutation,
  useDeleteCompanyPositionMutation,
} from "../../../services/api";

export default function PositionsSection({
  companyId,
  positions,
  isLoading,
  refetch,
}) {
  const [showModal, setShowModal] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [formData, setFormData] = useState({ name: "", description: "" });

  const [createPosition, { isLoading: isCreating }] = useCreateCompanyPositionMutation();
  const [updatePosition, { isLoading: isUpdating }] = useUpdateCompanyPositionMutation();
  const [deletePosition, { isLoading: isDeleting }] = useDeleteCompanyPositionMutation();

  const openAddModal = () => {
    setEditingPosition(null);
    setFormData({ name: "", description: "" });
    setShowModal(true);
  };

  const openEditModal = (position) => {
    setEditingPosition(position);
    setFormData({
      name: position.name || "",
      description: position.description || "",
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPosition(null);
    setFormData({ name: "", description: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    try {
      if (editingPosition) {
        await updatePosition({
          companyId,
          positionId: editingPosition._id,
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
        }).unwrap();
      } else {
        await createPosition({
          companyId,
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
        }).unwrap();
      }
      closeModal();
      refetch?.();
    } catch (err) {
      console.error("Failed to save position:", err);
    }
  };

  const handleDelete = async (positionId) => {
    if (!window.confirm("Are you sure you want to delete this position?")) return;

    try {
      await deletePosition({ companyId, positionId }).unwrap();
      refetch?.();
    } catch (err) {
      console.error("Failed to delete position:", err);
    }
  };

  return (
    <section className="team-section">
      <div className="team-section-header positions-header">
        <div>
          <h2 className="team-section-title">Team Positions</h2>
          <p className="team-section-subtitle">
            Create positions that team members can select when joining
          </p>
        </div>
        <button className="btn-add-position" onClick={openAddModal}>
          + Add Position
        </button>
      </div>

      {/* Positions List */}
      <div className="positions-list">
        {isLoading ? (
          <div className="positions-empty">Loading positions...</div>
        ) : positions.length === 0 ? (
          <div className="positions-empty">
            No positions created yet. Click "Add Position" to create one.
          </div>
        ) : (
          positions.map((position) => (
            <div key={position._id} className="position-item">
              <div className="position-info">
                <h4>{position.name}</h4>
                {position.description && <p>{position.description}</p>}
              </div>
              <div className="position-actions">
                <button
                  className="btn-edit"
                  onClick={() => openEditModal(position)}
                >
                  Edit
                </button>
                <button
                  className="btn-delete"
                  onClick={() => handleDelete(position._id)}
                  disabled={isDeleting}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="position-modal-overlay" onClick={closeModal}>
          <div className="position-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editingPosition ? "Edit Position" : "Add New Position"}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="positionName">Position Name *</label>
                <input
                  type="text"
                  id="positionName"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g., Chef, Manager, Server"
                  maxLength={100}
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label htmlFor="positionDesc">Description (Optional)</label>
                <textarea
                  id="positionDesc"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Brief description of this role"
                  maxLength={500}
                  rows={3}
                />
              </div>
              <div className="position-modal-actions">
                <button type="button" className="btn-cancel" onClick={closeModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-save"
                  disabled={isCreating || isUpdating || !formData.name.trim()}
                >
                  {isCreating || isUpdating ? "Saving..." : "Save Position"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
